import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { cn } from "../lib/cn";
import {
  readSettings,
  writeSettings,
  type UserSettings,
} from "../lib/settings";
import { DisplaySection } from "./settings/DisplaySection";
import { GettingStartedSection } from "./settings/GettingStartedSection";
import { LegalSection } from "./settings/LegalSection";
import { ProfileSection } from "./settings/ProfileSection";
import { TradingDefaultsSection } from "./settings/TradingDefaultsSection";

// Only the sections whose underlying behaviour is wired ship visibly.
// Display, Notifications, Data & imports, and About are intentionally
// commented out per the Phase 6 brief -- they'll light up as their
// downstream features land (theme persistence, mail delivery, broker
// import history, billing + diagnostic info). Re-adding a section is a
// single line + a component drop-in.
const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "trading-defaults", label: "Trading defaults" },
  { id: "display", label: "Display" },
  { id: "getting-started", label: "Getting started" },
  { id: "legal", label: "Legal" },
  // { id: "notifications", label: "Notifications" },
  // { id: "data-imports", label: "Data & imports" },
  // { id: "about", label: "About" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

const SECTION_IDS = new Set<SectionId>(SECTIONS.map((s) => s.id));
const DEFAULT_SECTION: SectionId = "profile";

function isSectionId(value: unknown): value is SectionId {
  return typeof value === "string" && SECTION_IDS.has(value as SectionId);
}

type SettingsSearch = { section?: SectionId } & Record<string, unknown>;

export function SettingsPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SettingsSearch;
  const active: SectionId = isSectionId(search.section)
    ? search.section
    : DEFAULT_SECTION;

  const [settings, setSettings] = useState<UserSettings>(() => readSettings());

  function handleSettingsChange(next: UserSettings) {
    setSettings(next);
    writeSettings(next);
  }

  function pickSection(id: SectionId) {
    void navigate({
      to: "/settings",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        section: id,
      }),
      replace: true,
    });
  }

  return (
    <section className="space-y-6 p-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Settings
        </h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Your profile and trading defaults. Additional sections land as their
          underlying features ship.
        </p>
      </header>

      <div className="grid gap-6 sm:grid-cols-[200px_1fr]">
        <nav
          aria-label="Settings sections"
          className="sm:sticky sm:top-6 sm:self-start"
        >
          <ul className="space-y-1">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => {
                    pickSection(section.id);
                  }}
                  aria-current={active === section.id ? "page" : undefined}
                  className={cn(
                    "block w-full rounded-control px-3 py-1.5 text-left text-sm font-medium transition-colors",
                    active === section.id
                      ? "bg-surface-container-high text-on-surface"
                      : "text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface",
                  )}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="min-w-0">
          {active === "profile" && (
            <ProfileSection settings={settings} onChange={handleSettingsChange} />
          )}
          {active === "trading-defaults" && (
            <TradingDefaultsSection
              settings={settings}
              onChange={handleSettingsChange}
            />
          )}
          {active === "display" && (
            <DisplaySection settings={settings} onChange={handleSettingsChange} />
          )}
          {active === "getting-started" && <GettingStartedSection />}
          {active === "legal" && <LegalSection />}
        </main>
      </div>
    </section>
  );
}
